<template>
  <div class="biele">
    <div 
      class="zoom-container"
      @mousedown="startDrag"
      @mousemove="onDrag"
      @mouseup="endDrag"
      @mouseleave="endDrag"
      @touchstart="startDrag"
      @touchmove="onDrag"
      @touchend="endDrag"
    >
      <!-- Apply transform on wrapper -->
      <div 
        class="zoom-wrapper"
        :style="{
          transform: `scale(${zoom}) translate(${posX}px, ${posY}px)`
        }"
      >
        <!-- Background schema -->
        <img :src="imgSrc" alt="Energy Lines" />

        <!-- Sensor overlays -->
        <div
          v-for="sensor in sensors"
          :key="sensor.id"
          class="sensor-label"
          :style="{
            top: sensorPositions[sensor.id]?.top,
            left: sensorPositions[sensor.id]?.left,
            color: sensor.offline 
              ? 'red' 
              : sensor.type === 'temperature' 
                ? 'orange' 
                : 'blue',
            fontSize: sensor.type === 'temperature' ? '1vw' : '0.8vw'
          }"
        >
          {{ sensor.type === "energy" ? sensor.name+":" : "" }}
          {{ sensor.offline 
            ? 'OFFLINE' 
            : sensor.value.toFixed(1) + (sensor.type === 'temperature' ? '°C' : 'kWh') }}
        </div>
      </div>

      <!-- Controls -->
      <div class="zoom-controls">
        <button @click="zoomIn">+</button>
        <button @click="zoomOut">-</button>
        <button @click="centerImage">⟳</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { sensors as sensorDefinitions } from "@/utils/userInfo.js";

export default {
  name: "WebpSchema",
  data() {
    return {
      imgSrc: '/img/schema.webp',
      zoom: 1,
      zoomStep: 0.3,
      minZoom: 1,
      maxZoom: 3,
      posX: 0,
      posY: 0,
      dragging: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      refreshInterval: null,
      sensorPositions: {
        T103: { top: "76%", left: "18%" },
        T600: { top: "12.5%", left: "10%" },
        T609: { top: "14%", left: "17%" },
        T411: { top: "52.2%", left: "25%" },
        T104: { top: "60%", left: "21%" },
        MEL: { top: "88%", left: "42%" },
        M31: { top: "55%", left: "51%" },
        M32: { top: "61%", left: "51%" },
        M41: { top: "98%", left: "28%" },
        M42: { top: "86.5%", left: "28%" },
        M51: { top: "78%", left: "28%" },
      },
      sensors: sensorDefinitions.map((s) => ({
        ...s,
        value: null,
        offline: true,
      })),
    };
  },
  mounted() {
    this.fetchData();
    this.refreshInterval = setInterval(this.fetchData, 60000);
  },
  beforeDestroy() {
    clearInterval(this.refreshInterval);
  },
  methods: {
    zoomIn() {
      if (this.zoom < this.maxZoom) this.zoom += this.zoomStep;
    },
    zoomOut() {
      if (this.zoom > this.minZoom) this.zoom -= this.zoomStep;
    },
    centerImage() {
      this.zoom = 1;
      this.posX = 0;
      this.posY = 0;
      this.lastX = 0;
      this.lastY = 0;
    },
    startDrag(e) {
      this.dragging = true;
      const evt = e.type.includes('touch') ? e.touches[0] : e;
      this.startX = evt.clientX - this.lastX;
      this.startY = evt.clientY - this.lastY;
    },
    onDrag(e) {
      if (!this.dragging) return;
      const evt = e.type.includes('touch') ? e.touches[0] : e;
      this.posX = evt.clientX - this.startX;
      this.posY = evt.clientY - this.startY;
    },
    endDrag() {
      this.dragging = false;
      this.lastX = this.posX;
      this.lastY = this.posY;
    },
    async fetchData() {
      const now = new Date();
      const start =
        new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
      const end = start + 86400 - 60;

      for (const sensor of this.sensors) {
        try {
          const response = await axios.post(
            "https://devel.ceelabs.com/energiareal/Data.php",
            { start, end, deviceID: sensor.id }
          );

          const stats = response.data.statistics;

          if (!stats || typeof stats.LAST !== "number") {
            sensor.offline = true;
            sensor.value = null;
          } else {
            sensor.offline = false;
            sensor.value = stats.LAST;
          }
        } catch (error) {
          sensor.offline = true;
          sensor.value = null;
        }
      }
    },
  },
};
</script>

<style scoped>
.biele {
  margin: 0 15px 20px;
  background-color: rgb(253, 253, 253);
  padding: 50px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.zoom-container {
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  cursor: grab;
}

.zoom-wrapper {
  position: relative;
  display: inline-block;
  transform-origin: center center;
}

.zoom-wrapper img {
  max-width: 80%;
  height: auto;
  border-radius: 12px;
  user-select: none;
  pointer-events: none;
}

/* Sensor labels scale with wrapper */
.sensor-label {
  position: absolute;
  transform: translate(-50%, -50%);
  font-weight: bold;
  white-space: nowrap;
  pointer-events: none;
}

.zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.zoom-controls button {
  padding: 5px 10px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #ccc;
  background: white;
  transition: background 0.2s;
}

.zoom-controls button:hover {
  background: #f0f0f0;
}
</style>
